import { ContentCopyRounded, DownloadRounded, EditNoteRounded, ExpandMoreRounded, RefreshRounded } from "@mui/icons-material";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Divider,
	Drawer,
	IconButton,
	Paper,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import { green, red, grey } from "@mui/material/colors";
import { GlobalState } from "context/GlobalContext";
import { getRelProtectedFileSignedUrl } from "data/globalData";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { useState } from "react";
import { downloadByUrl, jobStatusColor, jobsPollingTime } from "../data";

const ActiveJobsDrawer = ({ open, onClose, activeJobs, loading, onRefresh, downloadAnchorRef }) => {
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const [expandedJobId, setExpandedJobId] = useState(null);

	const toggleExpand = (jobId, status) => {
		if (status !== "completed" && status !== "failed") return;
		setExpandedJobId((prev) => (prev === jobId ? null : jobId));
	};

	const handleDownload = async (job) => {
		setLoadingOpen(true);
		try {
			const signedUrl = await getRelProtectedFileSignedUrl(job.download_url);
			downloadByUrl(signedUrl, job.name || `job_${job.job_id}`, downloadAnchorRef);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error), severity: "error" });
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleGetUrl = async (job) => {
		setLoadingOpen(true);
		try {
			const signedUrl = await getRelProtectedFileSignedUrl(job.download_url, 4 * 24 * 60 * 60);
			await navigator.clipboard.writeText(signedUrl);
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: "URL copied to clipboard (valid for 4 days)", severity: "success" });
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error), severity: "error" });
		} finally {
			setLoadingOpen(false);
		}
	};

	return (
		<Drawer anchor="right" open={open} onClose={onClose}>
			<Box width={480} maxWidth="100vw" pt={8}>
				<Box p={2} display="flex" justifyContent="space-between" alignItems="center">
					<Box>
						<Typography variant="h6">Active Download Jobs</Typography>
						<Typography variant="body2" color="text.secondary">
							Polling every {jobsPollingTime / 1000}s
						</Typography>
					</Box>
					<IconButton onClick={() => onRefresh()} disabled={loading}>
						{loading ? <CircularProgress size={20} /> : <RefreshRounded />}
					</IconButton>
				</Box>
				<Divider />
				<Box p={2}>
					{loading && !activeJobs.length ? (
						<Box minHeight={180} display="flex" alignItems="center" justifyContent="center" columnGap={1}>
							<CircularProgress size={20} />
							<Typography>Loading jobs...</Typography>
						</Box>
					) : activeJobs.length ? (
						<Stack spacing={1.5}>
							{activeJobs.map((job, index) => {
								const status = String(job.status || "queued").toLowerCase();
								const downloadUrl = job.download_url;
								const isExpandable = status === "completed" || status === "failed";
								const isExpanded = expandedJobId === (job.job_id || index);
								return (
									<Paper
										key={job.job_id || index}
										variant="outlined"
										sx={{ borderRadius: 1, overflow: "hidden", cursor: isExpandable ? "pointer" : "default" }}
										onClick={() => toggleExpand(job.job_id || index, status)}>
										<Box p={1.5}>
											<Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
												<Box minWidth={0} flex={1}>
													<Tooltip title={job.name || `Job ${index + 1}`} placement="bottom-start">
														<Typography variant="subtitle2" fontWeight={700} noWrap>
															{job.name || `Job ${index + 1}`}
														</Typography>
													</Tooltip>
													<Typography variant="caption" color="text.secondary" display="block">
														{job.job_id}
													</Typography>
												</Box>
												<Chip
													size="small"
													label={status}
													sx={{
														textTransform: "capitalize",
														backgroundColor: `${jobStatusColor[status] || grey[700]}15`,
														color: jobStatusColor[status] || grey[700],
													}}
												/>
												{isExpandable && (
													<ExpandMoreRounded
														sx={{
															fontSize: 18,
															color: grey[500],
															transition: "transform 0.2s",
															transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
														}}
													/>
												)}
											</Box>
										</Box>
										<Collapse in={isExpanded}>
											<Box px={1.5} pb={1.5}>
												<Divider sx={{ mb: 1.5 }} />
												{status === "failed" ? (
													<Box display="flex" alignItems="flex-start" gap={1}>
														<EditNoteRounded fontSize="small" sx={{ color: red[700], mt: 0.2 }} />
														<Typography
															variant="caption"
															color={red[700]}
															sx={{ wordBreak: "break-word" }}>
															{job.error_message || "Job failed with no error message."}
														</Typography>
													</Box>
												) : (
													<Box>
														<Typography variant="caption" color={green[700]} display="block" mb={1}>
															Job completed successfully.
														</Typography>
														{downloadUrl && (
															<Box display="flex" gap={1} flexWrap="wrap">
																<Button
																	size="small"
																	variant="contained"
																	startIcon={<DownloadRounded />}
																	onClick={(e) => {
																		e.stopPropagation();
																		handleDownload(job);
																	}}>
																	Download
																</Button>
																<Tooltip title="URL will be valid for 4 days" placement="bottom">
																	<Button
																		startIcon={<ContentCopyRounded />}
																		size="small"
																		variant="outlined"
																		onClick={(e) => {
																			e.stopPropagation();
																			handleGetUrl(job);
																		}}>
																		copy URL
																	</Button>
																</Tooltip>
															</Box>
														)}
													</Box>
												)}
											</Box>
										</Collapse>
									</Paper>
								);
							})}
						</Stack>
					) : (
						<Box minHeight={180} display="flex" alignItems="center" justifyContent="center">
							<Typography color="text.secondary">No active jobs found.</Typography>
						</Box>
					)}
				</Box>
			</Box>
		</Drawer>
	);
};

export default ActiveJobsDrawer;
